/** @jsxImportSource @emotion/react */

import { createForm, FormApi } from 'final-form'
import { Form as FinalForm } from 'react-final-form'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import arrayMutators from 'final-form-arrays'
import { FC, useEffect, useMemo, useState } from 'react'
import { ApolloError, useMutation } from '@apollo/client'
import Divider from '@mui/material/Divider'
import { CircularProgress, IconButton, InputAdornment } from '@mui/material'
import { InputField } from 'components/Input'
import { validateEmail, validateRequired } from 'utils/validation'
import { SelectField } from 'components/Select'
import { Select } from 'components/Select/Select'
import { RESET_PASSWORD, SET_PASSWORD_BY_ADMIN, UPDATE_USER_BY_ADMIN, GENERATE_UGC_DATA } from 'queries/users'
import { findOneUser_findOneUser as User } from '__generatedTypes__/findOneUser'
import {
  DentalSchoolsEnum,
  FacultyModeCohortYearEnum,
  PlatformName,
  RolesEnum,
  UGCCreationDataStatusEnum,
} from '__generatedTypes__/globalTypes'
import { ReactComponent as OpenEyeIcon } from 'assets/icons/open-eye.svg'
import { ReactComponent as CloseEyeIcon } from 'assets/icons/close-eye.svg'
import { updateUserByAdmin, updateUserByAdminVariables } from '__generatedTypes__/updateUserByAdmin'
import { setPasswordByAdmin, setPasswordByAdminVariables } from '__generatedTypes__/setPasswordByAdmin'
import { resetPassword, resetPasswordVariables } from '__generatedTypes__/resetPassword'
import { toast } from 'utils/toast'
import { useAuthContext } from 'utils/auth'
import { usePlatformsAndSubjects } from 'hooks/usePlatformsAndSubjects'
import { PlatformSlug } from 'constants/consts'
import { PageLoader } from 'components/PageLoader'
import { CheckboxField } from 'components/Checkbox'
import ConfirmationModal from 'components/Modals/ConfirmationModal'
import * as classes from './styles'
import { FacultyFields, getCohortItem } from './FacultyFields'
import { useFacultySchools, RefetchUser } from './hooks'
import { MFASettings } from './MFASettings'

type Props = {
  user: User
  isOwnAccount: boolean
  refetchUser: RefetchUser
}

type FormValues = {
  fullName: string
  email: string
  role: {
    id: RolesEnum
    title: string
  }
  facultyModeProduct?: {
    id: PlatformName
    title: string
  } | null
  facultyModeSchool?: {
    id: string
    title: string
  } | null
  facultyModeCohort?: {
    id: FacultyModeCohortYearEnum
    title: string
  } | null
  isBetaTester: boolean
  newPassword: string
}

const SELECTABLE_ROLE_ITEMS = [
  {
    id: RolesEnum.User,
    title: 'Student',
  },
  {
    id: RolesEnum.Admin,
    title: 'Administrator',
  },
  {
    id: RolesEnum.Teacher,
    title: 'Faculty Member',
  },
  {
    id: RolesEnum.AITrainer,
    title: 'AI Trainer',
  },
  {
    id: RolesEnum.Marketing,
    title: 'Marketing',
  },
  {
    id: RolesEnum.UGCCreator,
    title: 'UGC Creator',
  },
]

const ALL_ROLE_ITEMS = [
  ...SELECTABLE_ROLE_ITEMS,
  {
    id: RolesEnum.SuperAdmin,
    title: 'Super Administrator',
  },
]

const FACULTY_ROLES = [RolesEnum.Teacher, RolesEnum.User]

const EMAIL_ALREADY_TAKEN_ERROR_MESSAGE = 'An account with the given email already exists.'
const INVALID_EMAIL_ERROR_MESSAGE = 'Invalid email'
const INVALID_PASSWORD_ERROR_MESSAGE_STARTS = 'Password does not conform to policy'

const REFETCH_UGC_DATA_INTERVAL = 10000

export const UserInfo: FC<Props> = ({ user, isOwnAccount, refetchUser }) => {
  const { isAdmin } = useAuthContext()
  const [updateUser] = useMutation<updateUserByAdmin, updateUserByAdminVariables>(UPDATE_USER_BY_ADMIN)
  const [updatePassword] = useMutation<setPasswordByAdmin, setPasswordByAdminVariables>(SET_PASSWORD_BY_ADMIN)
  const [resetPassword] = useMutation<resetPassword, resetPasswordVariables>(RESET_PASSWORD)
  const [generateUGCData] = useMutation(GENERATE_UGC_DATA)
  const [isPasswordShown, setIsPasswordShown] = useState(false)

  const [isResettingLinkInFlight, setIsResetLinkInFlight] = useState(false)

  const [isGeneratingUGCData, setIsGeneratingUGCData] = useState(false)
  const [isConfirmUGCDataModalOpen, setIsConfirmUGCDataModalOpen] = useState(false)
  const [selectedUGCPlatform, setSelectedUGCPlatform] = useState<{ id: string; title: string } | null>(null)

  const { loading: platformsLoading, platforms } = usePlatformsAndSubjects()

  const { schools, fetchSchools, loading: schoolsLoading } = useFacultySchools()

  useEffect(() => {
    const setInitialSchool = async () => {
      if (!user.facultyModeProduct || !user.role || !FACULTY_ROLES.includes(user.role) || platformsLoading) return
      const res = await fetchSchools(user.facultyModeProduct)
      const school = res?.data?.findSchoolListByPlatform?.schoolArray?.find(
        (school) => school.key === user.facultyModeSchool
      )
      if (school) {
        form.change('facultyModeSchool', { id: school.key, title: school.value })
      }
    }

    setInitialSchool()
  }, [user, platformsLoading])

  useEffect(() => {
    if (
      [UGCCreationDataStatusEnum.PROCESSING, UGCCreationDataStatusEnum.PENDING].includes(
        user.ugcCreationDataStatus as UGCCreationDataStatusEnum
      ) &&
      refetchUser
    ) {
      setIsGeneratingUGCData(true)
      const interval = setInterval(async () => {
        const { data } = await refetchUser()
        if (
          ![UGCCreationDataStatusEnum.PROCESSING, UGCCreationDataStatusEnum.PENDING].includes(
            data?.findOneUser?.ugcCreationDataStatus as UGCCreationDataStatusEnum
          )
        ) {
          clearInterval(interval)
          setIsGeneratingUGCData(false)
          if (data?.findOneUser?.ugcCreationDataStatus === UGCCreationDataStatusEnum.COMPLETED) {
            toast.success('Stats generated successfully')
          } else if (data?.findOneUser?.ugcCreationDataStatus === UGCCreationDataStatusEnum.FAILED) {
            toast.error('Failed to generate stats')
          }
        }
      }, REFETCH_UGC_DATA_INTERVAL)
      return () => clearInterval(interval)
    }
  }, [user.ugcCreationDataStatus, refetchUser])

  const getInitialValues = () => {
    const { firstName, lastName, email, role: userRole } = user

    const selectedRole = ALL_ROLE_ITEMS.find((role) => role.id === userRole)
    const facultyModeSchool = schools?.find((school) => school.id === user.facultyModeSchool) || null

    return {
      fullName: `${firstName} ${lastName}`,
      email,
      role: selectedRole,
      facultyModeCohort: user.facultyModeCohort ? getCohortItem(user.facultyModeCohort) : null,
      facultyModeProduct: user.facultyModeProduct
        ? { id: user.facultyModeProduct, title: user.facultyModeProduct }
        : null,
      facultyModeSchool,
      isBetaTester: !!user.isBetaTester,
      newPassword: '',
    }
  }

  const handleSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
    const { dirty: shouldUpdateUser } = form.getState()

    const { fullName, email, role, newPassword } = values
    const [firstName, lastName, ...restName] = fullName.split(' ')

    try {
      await Promise.all([
        newPassword
          ? updatePassword({
              variables: {
                userId: user.id,
                newPassword,
              },
            })
          : Promise.resolve(),
        shouldUpdateUser
          ? await updateUser({
              variables: {
                updateUserInput: {
                  id: user.id,
                  firstName,
                  lastName: `${lastName} ${restName.join(' ')}`.trim(),
                  facultyModeCohort:
                    values?.facultyModeCohort && role.id !== RolesEnum.Teacher ? values.facultyModeCohort.id : null,
                  facultyModeProduct: values.facultyModeProduct?.id || null,
                  facultyModeSchool: (values.facultyModeSchool?.id || null) as DentalSchoolsEnum,
                  email,
                  role: role.id,
                  isBetaTester: values.isBetaTester,
                },
              },
            })
          : Promise.resolve(),
      ])
      toast.success('User info updated successfully')
      await refetchUser()
    } catch (error) {
      toast.error('Failed to update user info')

      const errors: Record<string, string> = {}

      if (error && typeof error === 'object' && 'graphQLErrors' in error) {
        const passwordError = (error.graphQLErrors as ApolloError['graphQLErrors']).find(({ message }) =>
          message.startsWith(INVALID_PASSWORD_ERROR_MESSAGE_STARTS)
        )

        if (passwordError) {
          errors.newPassword = passwordError.message
        }

        const existingEmailErrorIndex = (error.graphQLErrors as ApolloError['graphQLErrors']).findIndex(({ message }) =>
          [EMAIL_ALREADY_TAKEN_ERROR_MESSAGE].includes(message)
        )

        if (existingEmailErrorIndex !== -1) {
          errors.email = EMAIL_ALREADY_TAKEN_ERROR_MESSAGE
        }

        const invalidEmailErrorIndex = (error.graphQLErrors as ApolloError['graphQLErrors']).findIndex(
          ({ extensions }) => extensions?.originalError?.message?.includes?.(INVALID_EMAIL_ERROR_MESSAGE)
        )

        if (invalidEmailErrorIndex !== -1) {
          errors.email = INVALID_EMAIL_ERROR_MESSAGE
        }
      }

      return errors
    }
  }

  const form = useMemo(() => createForm({ initialValues: getInitialValues(), onSubmit: handleSubmit }), [user])

  const sendResetLink = async () => {
    try {
      setIsResetLinkInFlight(true)
      await resetPassword({
        variables: {
          email: user.email,
        },
      })
      toast.success('Reset link sent successfully')
    } catch (error) {
      toast.error('Failed to send reset link')
    } finally {
      setIsResetLinkInFlight(false)
    }
  }

  const handleGenerateUGCData = async () => {
    if (!selectedUGCPlatform) return

    try {
      setIsConfirmUGCDataModalOpen(false)
      setIsGeneratingUGCData(true)
      await generateUGCData({
        variables: {
          userId: user.id,
          platformId: selectedUGCPlatform.id,
        },
      })
      toast.success('Stats generation started')
      await refetchUser?.()
    } catch (error) {
      toast.error('Failed to start stats generation')
      setIsGeneratingUGCData(false)
    }
  }

  const handleChangePasswordVisibility = () => {
    setIsPasswordShown((isShown) => !isShown)
  }

  if (platformsLoading) {
    return <PageLoader />
  }

  return (
    <section css={classes.container}>
      <FinalForm<FormValues>
        mutators={{ ...arrayMutators }}
        form={form}
        onSubmit={handleSubmit}
        render={({ handleSubmit, form, values, submitting }) => (
          <form css={classes.form} onSubmit={handleSubmit}>
            <Typography variant="title3" css={classes.title}>
              Editing User Info
            </Typography>
            <Divider />
            <InputField
              name="fullName"
              validate={validateRequired}
              label="Full Name"
              placeholder="Enter Full Name"
              disabled={!isAdmin}
              fullWidth
            />
            <InputField
              name="email"
              label="Email"
              placeholder="Enter Email"
              fullWidth
              validate={validateEmail}
              disabled={!isAdmin}
            />
            <div css={classes.userRoleWrapper(user.role, values.role?.id)}>
              <SelectField
                name="role"
                label="Role"
                items={SELECTABLE_ROLE_ITEMS}
                placeholder="Role"
                fullWidth
                disabled={!isAdmin || isOwnAccount}
                css={classes.roleField}
                onChange={() => {
                  form.change('facultyModeProduct', null)
                  form.change('facultyModeSchool', null)
                  form.change('facultyModeCohort', null)
                }}
              />
              {FACULTY_ROLES.includes(values.role?.id) && (
                <FacultyFields
                  role={values.role?.id}
                  schools={schools || []}
                  schoolsLoading={schoolsLoading}
                  onProductChange={(product) => {
                    form.change('facultyModeSchool', null)
                    if (product) {
                      fetchSchools(product.id)
                    }
                  }}
                  disabled={!isAdmin}
                />
              )}
              {user.role === RolesEnum.UGCCreator && values.role?.id === RolesEnum.UGCCreator && (
                <>
                  <Select
                    label="UGC Creator Platform"
                    items={platforms
                      .filter(
                        (p) =>
                          ![
                            PlatformSlug.Admissions,
                            PlatformSlug.GREBooster,
                            PlatformSlug.NBEOPartOne,
                            PlatformSlug.MCATBooster,
                          ].includes(p.slug as PlatformSlug)
                      )
                      .map((p) => ({ id: p.id, title: p.title }))}
                    placeholder="Select UGC Creator Platform"
                    fullWidth
                    value={selectedUGCPlatform}
                    onChange={(platform) => setSelectedUGCPlatform(platform)}
                    disabled={!isAdmin || isGeneratingUGCData}
                    css={classes.roleField}
                  />
                  <Button
                    variant="outlined"
                    color="blue"
                    size="large"
                    fullWidth
                    css={classes.roleButton}
                    disabled={!isAdmin || submitting || isGeneratingUGCData || !selectedUGCPlatform}
                    onClick={() => setIsConfirmUGCDataModalOpen(true)}
                  >
                    {isGeneratingUGCData ? <CircularProgress size={16} color="inherit" /> : 'Generate Stats'}
                  </Button>
                </>
              )}
            </div>
            <CheckboxField name="isBetaTester" label="Is a Beta Tester" disabled={!isAdmin} />
            <Typography variant="title3" css={classes.title}>
              Password
            </Typography>
            <Divider />
            <InputField
              type={isPasswordShown ? 'text' : 'password'}
              name="newPassword"
              label="Set New Password"
              placeholder="Enter New Password"
              fullWidth
              disabled={!isAdmin}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleChangePasswordVisibility}
                    edge="end"
                    disableRipple
                    disableFocusRipple
                  >
                    {isPasswordShown ? <CloseEyeIcon /> : <OpenEyeIcon />}
                  </IconButton>
                </InputAdornment>
              }
            />
            <div>
              <Typography css={classes.resetPasswordLabel}>Password Reset</Typography>
              <Typography css={classes.resetPasswordCaption}>
                Send the user a link to reset their password. This will not change their password, nor will it force a
                change.{' '}
              </Typography>

              <Button
                variant="outlined"
                color="blue"
                size="large"
                fullWidth
                css={classes.sendLinkButton}
                disabled={!isAdmin || submitting || isResettingLinkInFlight}
                onClick={sendResetLink}
              >
                Send Reset Link
              </Button>
            </div>
            <div css={classes.buttonsWrapper}>
              <Button
                variant="outlined"
                color="blue"
                size="large"
                fullWidth
                disabled={!isAdmin || submitting}
                onClick={() => {
                  form.restart(getInitialValues())
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                color="blue"
                size="large"
                fullWidth
                disabled={!isAdmin || submitting}
              >
                Update
              </Button>
            </div>

            <MFASettings
              userId={user.id}
              email={user.email}
              isMandatoryModalEnabled={!!user.isMFAMandatory}
              refetchUser={refetchUser}
            />
          </form>
        )}
      >
        ComparisonTable
      </FinalForm>
      <ConfirmationModal
        open={isConfirmUGCDataModalOpen}
        title="Generate UGC Data"
        subtitle={`This will generate statistics data for the user's practice tests, question banks, and videos on ${selectedUGCPlatform?.title}. Continue?`}
        onClose={() => setIsConfirmUGCDataModalOpen(false)}
        onConfirm={handleGenerateUGCData}
        confirmText="Generate"
        closeText="Cancel"
        isBlueConfirm
        isLoading={isGeneratingUGCData}
      />
    </section>
  )
}
